import { useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { GroupBase } from 'react-select'
import { useApolloClient } from '@apollo/client'
import { debounce } from 'lodash-es'

import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'
import { SelectOption } from 'Components/Select/Select.types'

import {
  getDepotDevices,
  getDepotDevicesVariables,
} from 'Constants/graphql/queries/listing/__generated__/getDepotDevices'
import { GET_DEPOT_DEVICES } from 'Constants/graphql/queries/listing/GetDevices'

import { DepotDeviceSelectProps } from './DepotDeviceSelect.types'

export const DepotDeviceSelect = <
  Option extends SelectOption,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: DepotDeviceSelectProps<Option, IsMulti, Group>,
) => {
  const apolloClient = useApolloClient()
  const [lastOptions, setLastOptions] = useState<SelectOption[]>([])
  const { depotId, ...rest } = props

  const promiseOptions = useCallback(
    async (inputValue: string) => {
      const response = await apolloClient.query<
        getDepotDevices,
        getDepotDevicesVariables
      >({
        query: GET_DEPOT_DEVICES,
        variables: {
          id: depotId,
          searchTerm: inputValue,
        },
      })

      const stocklistItems = response?.data.depot.stocklistItems?.stocklistItems

      const parsedResponse: SelectOption[] =
        stocklistItems?.map(stocklistItem => {
          const { deviceDefinition } = stocklistItem
          const label = stocklistItem?.imei
            ? `${deviceDefinition?.name} IMEI ${stocklistItem?.imei}`
            : `${deviceDefinition?.name}`

          return {
            label,
            value: stocklistItem.id.toString(),
          }
        }) ?? []

      return new Promise<SelectOption[]>(resolve => {
        setLastOptions(parsedResponse)

        resolve(parsedResponse)
      })
    },
    [apolloClient, depotId],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadOptions = useCallback(
    debounce((inputText, callback) => {
      promiseOptions(inputText).then(options => callback(options))
    }, 300),
    [promiseOptions],
  )

  const defaultNoOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    if (inputValue.length < 2) {
      return (
        <FormattedMessage
          id="GlobalSearch_InputValidationMessage"
          values={{ limit: 3 }}
        />
      )
    }

    return <FormattedMessage id="No_devices_found" />
  }

  return (
    <Field
      {...rest}
      placeholder="Device Id, Apple iPhone 8, Samsung Galaxy S9..."
      selectProps={{
        isClearable: true,
        lastOptions,
        loadOptions,
        noOptionsMessage: defaultNoOptionsMessage,
      }}
      type={FieldTypeEnum.selectAsync}
    />
  )
}
